<template>
  <div>
    <UiBreadcrumbs :items="breadcrumbs" />

    <div class="panel">
      <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
          Create Content
        </h1>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Generate AI content using content types and location data
        </p>
      </div>

      <div class="p-6">
        <div class="max-w-3xl">
          <label for="content-type" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">Select Content Type</label>
          <UiFormContentTypeSelect
            id="content-type"
            v-model="selectedContentType"
            class="my-4"
            :allow-empty="false"
            :disabled="contentLoading"
            placeholder="Select Content Type"
            :api-function="getContentTypesForSelect"
            :object="true"
            :show-account-name="false" />

          <div v-if="!locationId">
            <label for="selected-location" class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">
              {{ selectedContentType?.locationBased ? 'Select Location *' : 'Select Location (Optional)' }}
            </label>
            <UiFormAsyncLocationSelect
              id="selected-location"
              v-model="locationId"
              :disabled="contentLoading"
              :api-function="getLocationsForSelect" />
          </div>

          <div v-if="selectedContentType && Object.keys(selectedContentType.dynamicInputs || {}).length > 0" class="mt-6">
            <div class="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div class="mb-4 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                Dynamic Inputs
              </div>
              <div v-for="[inputName, inputType] in selectedContentType.dynamicInputs" :key="inputName">
                <UiFormControl
                  :id="`${inputName}-input`"
                  :label="inputName"
                  class="!mb-4">
                  <UiFormInput
                    v-if="inputType === 'single'"
                    v-model="dynamicInputValues[inputName]"
                    :placeholder="inputName"
                    :disabled="contentLoading"
                    size="sm" />
                  <UiFormTextarea
                    v-if="inputType === 'list'"
                    v-model="dynamicInputValues[inputName]"
                    :placeholder="inputName"
                    :disabled="contentLoading"
                    size="sm" />
                  <div v-if="getLocationHint(inputName)" class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Default: {{ getLocationHint(inputName) }}
                  </div>
                </UiFormControl>
              </div>
            </div>
          </div>

          <div class="mt-6">
            <button
              v-if="!contentLoading"
              type="button"
              class="btn"
              :class="isFormValid ? 'btn-success' : 'btn-default'"
              :disabled="!isFormValid"
              @click="createContent()">
              Generate Article
            </button>

            <button
              v-if="contentLoading"
              disabled
              type="button"
              class="btn btn-default">
              <UiSpinner class="mr-3" />
              Generating...
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { createToast } from 'mosha-vue-toastify'
import { UiFormControl } from '@ui/form/control'
import { UiFormContentTypeSelect } from '@ui/form/content_type_select'
import { UiFormAsyncLocationSelect } from '@ui/form/location_select'
import { UiFormInput } from '@ui/form/input'
import { UiFormTextarea } from '@ui/form/textarea'
import UiSpinner from '@ui/spinner.vue'
import UiBreadcrumbs from '@/vue/components/ui/breadcrumbs.vue'
import { getLocationsForSelect, getLocation } from '@/api/clients/locations'
import { getContentTypesForSelect } from '@/api/clients/content_types'
import { generateContentEntity } from '@/api/clients/content_entities'

const props = defineProps({
  locationId: String,
  contentTypeId: String
})

const breadcrumbs = computed(() => [
  {
    title: 'AI Content',
    path: '/clients/ai_content'
  },
  {
    title: 'Content Entities',
    path: '/clients/ai_content/content_entities'
  },
  {
    title: 'New'
  }
])

const selectedContentType = ref(null)
const dynamicInputValues = ref({})
const locationId = ref(props.locationId)
const locationData = ref(null)
const contentLoading = ref(false)

// Load content type if contentTypeId prop is provided
onMounted(async () => {
  if (props.contentTypeId) {
    try {
      const { data } = await getContentTypesForSelect({ search: '' })
      selectedContentType.value = data.find((ct) => ct.id === props.contentTypeId)
    } catch (error) {
      console.error('Failed to load content type:', error)
    }
  }
})

watch(locationId, async (newLocationId) => {
  if (newLocationId) {
    try {
      const response = await getLocation({ id: newLocationId })
      locationData.value = response.data
    } catch (error) {
      console.error('Failed to load location data:', error)
      locationData.value = null
    }
  } else {
    locationData.value = null
  }
}, { immediate: true })

// Get location hint for dynamic input
const getLocationHint = (inputName) => {
  if (!locationData.value) return null

  const value = locationData.value[inputName]
  return value && typeof value === 'string' ? value : null
}

const isFormValid = computed(() => {
  if (!selectedContentType.value) return false

  if (selectedContentType.value.locationBased && !locationId.value) return false

  return true
})

const createContent = () => {
  if (!isFormValid.value) return

  contentLoading.value = true
  createToast('Content creating... you will be redirected on completion.', { type: 'success' })
  const payload = {
    contentTypeId: selectedContentType.value.id,
    locationId: locationId.value,
    dynamicInputs: dynamicInputValues.value
  }

  generateContentEntity(payload)
    .then((response) => {
      window.location = `/clients/ai_content/content_entities/${response.data.id}`
    })
    .catch((error) => {
      createToast(error.response?.data?.error || 'Unexpected error occurred.', { type: 'danger' })
      console.log(error)
    }).finally(() => {
      contentLoading.value = false
    })
}
</script>
